import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { UTMQueryParams } from "../offering/utm";
import { isValidTime } from "../lib/datetime";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_id",
  "utm_term",
  "utm_content",
];

export interface UrlParams {
  reset?: boolean;
  keepOnReset?: string[];
}

const TIME_KEYS = ["startTime", "endTime"];

// Links are copied through mail clients, spreadsheets and shell scripts, any of
// which can leave whitespace on a value, so surrounding whitespace says nothing
// about what the sender meant and is trimmed away. What survives the trim has to
// be a time we can actually book: an unusable one is dropped rather than carried
// forward, because every parser downstream reads the leading digits and ignores
// the rest, and the mismatch would surface only when the booking is written —
// after the customer has paid. Dropping it leaves the picker unset, where the
// existing validation asks the customer to choose a time.
function sanitizeTimeParam(value: string): string | undefined {
  const trimmed = value.trim();

  return isValidTime(trimmed) ? trimmed : undefined;
}

export function queryStringToParams(queryString: string) {
  const searchParams = new URLSearchParams(queryString);
  const result: { [key: string]: string } = {};
  const entries = searchParams.entries();

  let current = entries.next();

  while (current.value) {
    const [key, value] = current.value;
    // When an email address contains a + character (e.g., "user+label@gmail.com"),
    // browsers automatically encode it in URLs. When URLSearchParams decodes it,
    // it converts the + back to a space, which breaks the email address.
    // The simplest solution is to convert these spaces back to + characters
    // only for email parameters, preserving the original email format.
    if (key === "email") {
      result[key] = value.replace(/ /g, "+");
    } else if (TIME_KEYS.includes(key)) {
      const time = sanitizeTimeParam(value);
      if (time) result[key] = time;
    } else {
      result[key] = value;
    }
    current = entries.next();
  }

  return result;
}

export function useQueryString<T extends object = any>(): T {
  const { search: searchString } = useLocation();

  const params = useMemo(() => {
    return queryStringToParams(searchString.toString());
  }, [searchString]);

  // @ts-ignore
  return params;
}

export function useUTMQueryString(): string | undefined {
  const queryParams: UTMQueryParams = useQueryString();
  const utmOnlySearchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(queryParams)) {
    if (!key) {
      continue;
    }

    if (!UTM_KEYS.includes(key)) {
      continue;
    }

    if (value) {
      utmOnlySearchParams.set(key, value);
    } else {
      utmOnlySearchParams.delete(key);
    }
  }

  const utmOnlySearchString = utmOnlySearchParams.toString();

  return utmOnlySearchString ? `?${utmOnlySearchString}` : "";
}

export function stripParamsFromPath(path: string, keys: string[]): string {
  if (keys.length === 0) return path;
  const [pathname, search] = path.split("?", 2);
  if (!search) return path;
  const searchParams = new URLSearchParams(search);
  for (const key of keys) {
    searchParams.delete(key);
  }
  const next = searchParams.toString();
  return next ? `${pathname}?${next}` : pathname;
}

/** The path with each given param set on it, over any it already carries.
 *  Unset values are left off. */
export function setParamsOnPath(
  path: string,
  params: Record<string, string | number | undefined>
): string {
  const [pathname, search] = path.split("?", 2);
  const searchParams = new URLSearchParams(search);
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") {
      searchParams.set(key, String(value));
    }
  }
  const next = searchParams.toString();
  return next ? `${pathname}?${next}` : pathname;
}

export function buildPathWithParams(
  targetPath: string,
  filter?: { [key: string]: any } | null
): string {
  const targetUrl = new URL(targetPath, window.location.origin);
  const targetPathParams = targetUrl.searchParams;

  const currentURLParams = new URLSearchParams(window.location.search);
  let combinedSearchParams = new URLSearchParams();

  // Keep the input path's search string if any
  let entries = targetPathParams.entries();
  let current = entries.next();
  while (current.value) {
    const [key, value] = current.value;

    if (key && value) {
      combinedSearchParams.set(key, value);
    }

    current = entries.next();
  }

  // Append the current location path's search string if any
  entries = currentURLParams.entries();
  current = entries.next();
  while (current.value) {
    const [key, value] = current.value;

    if (key && value) {
      combinedSearchParams.set(key, value);
    }

    current = entries.next();
  }

  // Replace current filter with the new filter as search string if any
  if (filter) {
    const reset = !!filter.reset;
    const keepOnReset = filter.keepOnReset || [];

    delete filter.reset;
    delete filter.whitelist;

    for (const [key, value] of Object.entries(filter)) {
      if (!key) {
        continue;
      }

      if (value) {
        combinedSearchParams.set(key, value);
      } else {
        combinedSearchParams.delete(key);
      }
    }

    // if there is a flag in filter reset = true
    // then we will keep only UTM related params
    // and remove everything else, except for those specified in keepOnReset
    if (reset) {
      const newSearchParams = new URLSearchParams();
      const entries = combinedSearchParams.entries();

      let current = entries.next();

      while (current.value) {
        const [key, value] = current.value;
        if (UTM_KEYS.includes(key) || keepOnReset.includes(key)) {
          newSearchParams.set(key, value);
        }
        current = entries.next();
      }

      combinedSearchParams = newSearchParams;
    }
  }

  const combinedSearchString = combinedSearchParams.toString();

  return `${targetUrl.pathname}${
    combinedSearchString ? `?${combinedSearchString}` : ""
  }`;
}
